import { Dialog } from '@angular/cdk/dialog';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isPast, isSameMinute } from 'date-fns';
import { catchError, of, switchMap, timer } from 'rxjs';
import { AlarmsService } from '@API/alarms/alarms.service';
import { Alarm } from '@interfaces/alarm';
import { ServerListResponse } from '@utils/interfaces/serverResponses';
import { AlarmModalComponent } from '@alarms/components/alarm-modal/alarm-modal.component';
import { SessionService } from '@utils/services/session.service';

@Component({
  selector: 'alarm-emitter',
  standalone: true,
  template: '',
})
export class AlarmEmitterComponent implements OnInit {
  private static readonly POLL_INTERVAL_MS = 60_000;

  private readonly _dialog = inject(Dialog);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _alarmService = inject(AlarmsService);
  private readonly _sessionService = inject(SessionService);

  ngOnInit(): void {
    timer(0, AlarmEmitterComponent.POLL_INTERVAL_MS)
      .pipe(
        switchMap(() =>
          this._alarmService.getNotificationList(1, 10).pipe(
            catchError(() =>
              of<ServerListResponse<Alarm[]>>({ count: 0, data: [] }),
            ),
          ),
        ),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(({ data }) => {
        this._verifyAlarms(data);
      });
  }

  private _verifyAlarms(alarmsList: Alarm[]): void {
    const actualDate = new Date();

    for (const alarm of alarmsList) {
      const alarmDate = new Date(alarm.alarm_datetime);

      if (isPast(actualDate) && !isSameMinute(alarmDate, actualDate)) {
        continue;
      }

      const isAssignedUser =
        alarm.user_in_charge.id === this._sessionService.user?.id;
      const isAdminUnassigned =
        alarm.user_in_charge.id === null &&
        this._sessionService.user?.type === 'admin';

      if (
        isSameMinute(alarmDate, actualDate) &&
        (isAssignedUser || isAdminUnassigned)
      ) {
        this._showNotification(alarm);
        return;
      }
    }
  }

  private _showNotification(alarm: Alarm): void {
    const modalRef = this._dialog.open(AlarmModalComponent, {
      data: alarm,
      width: '600px',
    });

    modalRef.closed
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (wasRead) => {
          if (wasRead) {
            alarm.was_read = true;
          }
        },
      });
  }
}
